import axios, { type AxiosError, type AxiosResponse } from "axios";
import {
	attrs,
	browserName,
	browserVersion,
	deviceType,
	formatVitalValue,
	osName,
	pageContext,
	parseEnabled,
	parseSampleRate,
	referrerCategory,
	resolveEnvironment,
	resourceType,
	safePageUrl,
	sanitizeRequestUrl,
	sessionSampled,
	shortVersion,
	siteFromService,
	viewportClass,
	type PageContext
} from "~/utils/observability";

/**
 * Real-user Core Web Vitals and frontend errors.
 *
 * Faro's built-in web-vitals measurement does not carry page route, page
 * type, or rating, so those are recorded with the `web-vitals` package and
 * sent as `web_vital` events. Faro still collects exceptions.
 *
 * LCP, INP, CLS, TTFB, and FCP are browser measurements. When the browser
 * reports a soft navigation, the event uses that navigation's URL. Otherwise
 * the measurement stays on the document that was loaded, even if Vue Router
 * has since changed the page. Those route changes emit `route_navigation`.
 *
 * A session is sampled once (sessionStorage). Exceptions, broken resources,
 * and failed HTTP calls are always sent.
 */

const LOCAL_COLLECTOR = "http://localhost:12347/collect";
const PRODUCTION_COLLECTOR =
	"https://alloy-production-e9ff.up.railway.app/collect";

const ERROR_EVENTS = new Set(["resource_load_error", "http_error"]);

const IGNORE_ERRORS = [
	"Page Not Found",
	"AxiosError: Request aborted",
	/ERR_CANCELED/,
	/Load failed/,
	/Failed to fetch/,
	/NetworkError/,
	/Failed to fetch dynamically imported module/,
	/Importing a module script failed/,
	/Unable to preload CSS for/,
	/\/_nuxt\/builds\/meta\//
];

type PushFn = (name: string, attributes: Record<string, string>) => void;

type RumConfig = {
	observabilityEnabled?: unknown;
	faroCollectorUrl?: string;
	serviceName?: string;
	appEnvironment?: string;
	appVersion?: string;
	rumSampleRate?: unknown;
	faro?: {
		enabled?: unknown;
		url?: string;
		environment?: string;
		version?: string;
		sampleRate?: unknown;
	};
};

function collectorFor(configured: string, environment: string) {
	if (configured) return configured.replace(/\/$/, "");
	return environment === "local" ? LOCAL_COLLECTOR : PRODUCTION_COLLECTOR;
}

function roundMs(value: number) {
	if (!Number.isFinite(value)) return "0";
	return String(Math.max(0, Math.round(value)));
}

export default defineNuxtPlugin(() => {
	const runtime = useRuntimeConfig().public as RumConfig;
	const legacy = runtime.faro;
	const enabled = parseEnabled(
		runtime.observabilityEnabled ?? legacy?.enabled ?? true
	);
	if (!enabled) return;

	const environment = resolveEnvironment(
		String(runtime.appEnvironment || legacy?.environment || ""),
		location.hostname
	);
	const collector = collectorFor(
		String(runtime.faroCollectorUrl || legacy?.url || ""),
		environment
	);
	const serviceName = runtime.serviceName || "stockstotrade-web";
	const version = shortVersion(runtime.appVersion || legacy?.version);
	const sampleRate = parseSampleRate(
		runtime.rumSampleRate ?? legacy?.sampleRate,
		environment
	);
	const sampled = sessionSampled(sampleRate, window.sessionStorage);
	const site = siteFromService(serviceName);
	const documentPage = pageContext(location.href);

	const pending: Array<{ name: string; attributes: Record<string, string> }> =
		[];
	let push: PushFn | null = null;

	const send = (name: string, attributes: Record<string, string>) => {
		if (push) {
			push(name, attributes);
			return;
		}
		if (pending.length < 50) pending.push({ name, attributes });
	};

	const base = (page: PageContext) =>
		attrs({
			site,
			page_host: location.hostname,
			page_path: page.path,
			page_route: page.route,
			page_type: page.type,
			page_url: safePageUrl(`${location.origin}${page.path}`),
			environment,
			version,
			device_type: deviceType(
				navigator.userAgent,
				window.innerWidth,
				navigator.maxTouchPoints || 0
			),
			browser: browserName(navigator.userAgent),
			browser_version: browserVersion(navigator.userAgent),
			os: osName(navigator.userAgent),
			connection_type:
				(
					navigator as Navigator & {
						connection?: { effectiveType?: string };
					}
				).connection?.effectiveType || "",
			viewport: viewportClass(window.innerWidth),
			referrer_category: referrerCategory(document.referrer, location.hostname)
		});

	installResourceTracking(send, () => base(pageContext(location.href)));
	installHttpTracking(send, collector, () => base(pageContext(location.href)));

	if (sampled) {
		installRouteTiming(send, base);
		void import("web-vitals").then((webVitals) => {
			const report = (metric: {
				name: string;
				value: number;
				rating?: string;
				navigationType?: string;
				navigationURL?: string;
			}) => {
				const page = pageForVital(metric.navigationURL, documentPage);
				send(
					"web_vital",
					attrs({
						...base(page),
						metric: metric.name,
						value: formatVitalValue(metric.name, metric.value),
						rating: metric.rating || "good",
						navigation_type: metric.navigationType || "",
						scope:
							metric.navigationType === "soft-navigation"
								? "soft-navigation"
								: "document"
					})
				);
			};
			const opts = { reportSoftNavs: true };
			webVitals.onLCP(report, opts);
			webVitals.onINP(report, opts);
			webVitals.onCLS(report, opts);
			webVitals.onFCP(report, opts);
			webVitals.onTTFB(report, opts);
		});
		installNavigationTiming(send, () =>
			attrs({
				...base(documentPage),
				scope: "document"
			})
		);
	}

	void import("@grafana/faro-web-sdk").then((faroSdk) => {
		const faro = faroSdk.initializeFaro({
			url: collector,
			app: {
				name: serviceName,
				environment,
				version
			},
			instrumentations: [
				new faroSdk.ErrorsInstrumentation(),
				new faroSdk.SessionInstrumentation(),
				new faroSdk.ViewInstrumentation()
			],
			ignoreErrors: IGNORE_ERRORS,
			beforeSend(item) {
				if (item.type === "exception") return item;
				if (item.type === "event") {
					const name = (item.payload as { name?: string }).name;
					if (name && ERROR_EVENTS.has(name)) return item;
					if (!sampled) return null;
				} else if (!sampled) {
					return null;
				}
				return item;
			}
		});
		if (!faro) return;
		push = (name, attributes) => faro.api.pushEvent(name, attributes);
		for (const item of pending) push(item.name, item.attributes);
		pending.length = 0;
	});
});

function pageForVital(navigationURL: string | undefined, fallback: PageContext) {
	if (!navigationURL) return fallback;
	try {
		const url = new URL(navigationURL);
		if (url.origin !== location.origin) return fallback;
		return pageContext(url.pathname);
	} catch {
		return fallback;
	}
}

function installResourceTracking(
	send: PushFn,
	context: () => Record<string, string>
) {
	const flag = "__rumResourceTracking";
	if ((window as unknown as Record<string, boolean>)[flag]) return;
	(window as unknown as Record<string, boolean>)[flag] = true;
	const reported = new Set<string>();

	window.addEventListener(
		"error",
		(event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			const tag = target.tagName.toLowerCase();
			const source =
				target instanceof HTMLLinkElement
					? target.href
					: target instanceof HTMLImageElement ||
						  target instanceof HTMLScriptElement ||
						  target instanceof HTMLIFrameElement
						? target.src
						: "";
			if (!source) return;
			const resource = sanitizeRequestUrl(source);
			if (!resource || reported.has(resource)) return;
			reported.add(resource);
			send("resource_load_error", {
				...context(),
				resource,
				resourceType: resourceType(
					tag,
					target.getAttribute("rel") || "",
					target.getAttribute("as") || ""
				)
			});
		},
		true
	);
}

function installHttpTracking(
	send: PushFn,
	collector: string,
	context: () => Record<string, string>
) {
	const startedAt = new WeakMap<object, number>();
	const report = (
		status: number,
		method: string,
		rawUrl: string,
		config: object | undefined
	) => {
		if (status < 400) return;
		if (!rawUrl || rawUrl.startsWith(collector)) return;
		const url = sanitizeRequestUrl(rawUrl);
		if (!url) return;
		const began = config ? startedAt.get(config) : undefined;
		send("http_error", {
			...context(),
			url,
			method: (method || "GET").toUpperCase(),
			status: String(status),
			duration_ms: roundMs(performance.now() - (began ?? performance.now()))
		});
	};

	const axiosFlag = "__rumAxiosTracking";
	const marked = axios as unknown as Record<string, boolean>;
	if (!marked[axiosFlag]) {
		marked[axiosFlag] = true;
		const rawUrlFor = (config?: AxiosResponse["config"]) => {
			if (!config?.url) return "";
			return /^https?:\/\//i.test(config.url)
				? config.url
				: `${config.baseURL || ""}${config.url}`;
		};
		axios.interceptors.request.use((config) => {
			startedAt.set(config, performance.now());
			return config;
		});
		axios.interceptors.response.use(
			(response: AxiosResponse) => {
				if (response.status >= 400) {
					report(
						response.status,
						response.config?.method || "get",
						rawUrlFor(response.config),
						response.config
					);
				}
				return response;
			},
			(error: AxiosError) => {
				if (error.response) {
					report(
						error.response.status,
						error.config?.method || "get",
						rawUrlFor(error.config),
						error.config
					);
				}
				return Promise.reject(error);
			}
		);
	}

	const fetchFlag = "__rumFetchTracking";
	const browserWindow = window as unknown as Record<string, boolean> & Window;
	if (browserWindow[fetchFlag]) return;
	browserWindow[fetchFlag] = true;
	const original = window.fetch.bind(window);
	window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
		const startedAt = performance.now();
		const rawUrl =
			typeof input === "string"
				? input
				: input instanceof URL
					? input.href
					: input.url;
		const method =
			init?.method ||
			(input instanceof Request ? input.method : "GET");
		const response = await original(input, init);
		report(response.status, method, rawUrl, startedAt);
		return response;
	};
}

function installNavigationTiming(
	send: PushFn,
	context: () => Record<string, string>
) {
	const emit = () => {
		const nav = performance.getEntriesByType("navigation")[0] as
			| PerformanceNavigationTiming
			| undefined;
		if (!nav) return;
		const tls =
			nav.secureConnectionStart > 0
				? nav.connectEnd - nav.secureConnectionStart
				: 0;
		send("navigation_timing", {
			...context(),
			dns_ms: roundMs(nav.domainLookupEnd - nav.domainLookupStart),
			tcp_ms: roundMs(nav.connectEnd - nav.connectStart),
			tls_ms: roundMs(tls),
			ttfb_ms: roundMs(nav.responseStart - nav.requestStart),
			dom_interactive_ms: roundMs(nav.domInteractive),
			dom_content_loaded_ms: roundMs(nav.domContentLoadedEventEnd),
			load_event_ms: roundMs(nav.loadEventEnd),
			duration_ms: roundMs(nav.duration),
			navigation_type: nav.type
		});
	};

	if (document.readyState === "complete") emit();
	else window.addEventListener("load", emit, { once: true });
}

function installRouteTiming(
	send: PushFn,
	base: (page: PageContext) => Record<string, string>
) {
	const router = useRouter();
	let started = 0;
	router.beforeEach((to, from) => {
		if (from.matched.length === 0) return;
		if (pageContext(to.path).path === pageContext(from.path).path) return;
		started = performance.now();
	});
	router.afterEach((to, from) => {
		if (!started || from.matched.length === 0) return;
		const next = pageContext(to.path);
		const previous = pageContext(from.path);
		if (next.path === previous.path) return;
		const duration = performance.now() - started;
		started = 0;
		send(
			"route_navigation",
			attrs({
				...base(next),
				duration_ms: roundMs(duration),
				navigation_type: "client",
				scope: "route",
				from_path: previous.path,
				from_route: previous.route
			})
		);
	});
}
